<template>
  <LoadingDiv v-if="!isReady" />

  <template v-else>
    <section class="status-workspace">
      <div class="status-head">
        <div>
          <label class="active teal-text">{{ cat == "tpa" ? "TPA" : "Policy" }} Status</label>
          <h5>{{ statusLabel }}</h5>
          <p>{{ statusMessage }}</p>
        </div>

        <span class="status-pill" :class="{ pending: !status }">
          <i class="material-icons">{{ status ? "check_circle" : "schedule" }}</i>
          {{ status ? "Active" : closedOrPending }}
        </span>
      </div>

      <div class="status-error-alert" v-if="error">
        <i class="material-icons">error_outline</i>
        <span>{{ error }}</span>
      </div>

      <div class="status-grid">
        <article class="status-card">
          <div class="card-title-row">
            <div>
              <label class="active teal-text">Current Window</label>
              <h6>{{ cat == "tpa" ? "TPA" : "Policy" }} Year</h6>
            </div>
            <i class="material-icons card-title-icon">date_range</i>
          </div>

          <div class="date-grid">
            <div class="date-box">
              <span>Starts</span>
              <strong>{{ formatDate(history.start) }}</strong>
            </div>
            <div class="date-box">
              <span>Ends</span>
              <strong>{{ formatDate(history.end) }}</strong>
            </div>
          </div>
        </article>

        <article class="status-card action-card">
          <div>
            <label class="active teal-text">Renewal Action</label>
            <h6>Add Another Policy Year</h6>
            <p>
              Open a new policy year when this client is within the renewal
              window. This carries forward the current policy rules.
            </p>
          </div>

          <button
            class="btn waves-effect waves-light"
            :disabled="!canAddPolicy || isRenewing"
            @click.prevent="handleRenewalConfirmOpen"
          >
            <i class="tiny material-icons">add_circle</i>
            {{ isRenewing ? "Renewing..." : "Add Policy Year" }}
          </button>

          <small v-if="!canAddPolicy">
            Renewal becomes available close to the current policy end date.
          </small>
        </article>
      </div>

      <article class="history-card">
        <div class="card-title-row">
          <div>
            <label class="active teal-text">History</label>
            <h6>Previous Policy Years</h6>
          </div>
          <i class="material-icons card-title-icon">history</i>
        </div>

        <div class="history-list" v-if="previousPolicies.length">
          <div class="history-item" v-for="old in previousPolicies" :key="old.id">
            <span>{{ cat == "tpa" ? "TPA" : "Policy" }} Year</span>
            <strong>
              {{ formatDate(old.start_date || old.start) }} -
              {{ formatDate(old.end_date || old.end) }}
            </strong>
          </div>
        </div>

        <div class="empty-state" v-else>
          <i class="material-icons">event_available</i>
          <h6>No previous policy years recorded.</h6>
        </div>
      </article>
    </section>
  </template>

  <Teleport to="body">
    <div
      class="renewal-confirm-overlay"
      @click.self="handleRenewalConfirm(false)"
      v-if="isRenewalConfirmOpen"
    >
      <section
        class="renewal-confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="renewal-confirm-title"
      >
        <div class="renewal-confirm-content">
          <h4 id="renewal-confirm-title">{{ alertTitle }}</h4>
          <p>{{ alertMessage }}</p>
        </div>
        <div class="renewal-confirm-footer">
          <button
            type="button"
            class="renewal-confirm-btn is-cancel"
            @click.prevent="handleRenewalConfirm(false)"
          >
            Cancel
          </button>
          <button
            type="button"
            class="renewal-confirm-btn is-confirm"
            :disabled="isRenewing"
            @click.prevent="handleRenewalConfirm(true)"
          >
            Confirm
          </button>
        </div>
      </section>
    </div>
  </Teleport>
</template>

<script>
import { computed, onMounted, ref } from "vue";
import getItemsOfItems from "@/composables/getItemsOfItems";
import postItem from "@/composables/postItem";
import LoadingDiv from "@/components/LoadingDiv.vue";

export default {
  name: "StatusClientComponent",
  components: { LoadingDiv },
  props: ["cat", "clientId"],
  setup(props) {
    const alertTitle = ref("Confirm New Policy Year");
    const alertMessage = ref(
      "Starting a new policy year will automatically deactivate the current policy year for this client. Do you want to continue?"
    );
    const response = ref(null);
    const isReady = ref(false);
    const error = ref(null);
    const currUrl = ref(null);
    const currHook = ref(null);
    const show = ref(false);
    const isRenewalConfirmOpen = ref(false);
    const isRenewing = ref(false);
    const renewalDebug = ref(null);
    const genericLoadError = "Unable to load policy status right now. Please try again.";
    const genericRenewalError = "Unable to renew this policy right now. Please try again.";

    const captureRenewalIssue = (label, detail) => {
      renewalDebug.value = { label, detail };
    };

    // get function
    const getItemsFromApi = (table, id) => {
      (async () => {
        const { res, err } = await getItemsOfItems(table, id);

        if (res.value) {
          const policies = Array.isArray(res.value.data) ? res.value.data : [];
          const currentPolicy = policies.find((policy) => policy.status == "a")
            || policies.find((policy) => policy.status == "p")
            || policies[0]
            || null;

          response.value = currentPolicy
            ? {
                ...currentPolicy,
                start: currentPolicy.start_date || currentPolicy.start,
                end: currentPolicy.end_date || currentPolicy.end,
                server_date: res.value.server || new Date().toISOString().split("T")[0],
                history: policies,
              }
            : null;
        } else {
          captureRenewalIssue("policy status load failed", err.value);
          error.value = genericLoadError;
        }

        // page is ready
        isReady.value = true;
      })();
    };

    const formatDate = (date) => {
      if (!date) return "Not available";

      return new Date(date).toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    };

    // starter
    onMounted(() => {
      // start from the top
      getStatus();
    });

    // prinipals
    const getStatus = () => {
      error.value = null;
      // loader
      isReady.value = false;
      // set current Url
      currUrl.value = "policy/client";
      // set current Hook
      currHook.value = "?";
      // get items from function
      getItemsFromApi(currUrl.value, props.clientId);
    };

    // handle new policy
    const handleNewPolicy = async () => {
      if (!response.value || isRenewing.value) return;

      error.value = null;
      renewalDebug.value = null;
      isRenewing.value = true;

      try {
        const newStartDate = new Date(response.value.end);

        if (Number.isNaN(newStartDate.getTime())) {
          throw new Error("Invalid policy end date");
        }

        newStartDate.setDate(newStartDate.getDate() + 1);

        const startDate = newStartDate.toISOString().split("T")[0];
        const endDate = new Date(startDate);
        endDate.setFullYear(endDate.getFullYear() + 1);
        endDate.setDate(endDate.getDate() - 1);

        if (Number.isNaN(endDate.getTime())) {
          throw new Error("Invalid renewal end date");
        }

        // post update
        const { res, err } = await postItem("operator/client/restart/policy", {
          client_id: props.clientId,
          start_date: startDate,
          end_date: endDate.toISOString().split("T")[0],
          cycle: response.value.cycle,
          family_size: response.value.family_size,
          principal_age: response.value.principal_age,
          dependant_age: response.value.dependant_age,
        });

        if (res.value?.data) {
          // restart
          getStatus();
        } else {
          captureRenewalIssue("policy renewal failed", err.value || res.value);
          error.value = genericRenewalError;
        }
      } catch (renewalError) {
        captureRenewalIssue("policy renewal failed", renewalError);
        error.value = genericRenewalError;
      } finally {
        isRenewing.value = false;
      }
    };

    const handleRenewalConfirmOpen = () => {
      if (!canAddPolicy.value) {
        return;
      }

      isRenewalConfirmOpen.value = true;
    };

    const handleRenewalConfirm = (confirm) => {
      isRenewalConfirmOpen.value = false;

      if (confirm) {
        handleNewPolicy();
      }
    };

    // handle download
    const handleDownload = () => {
      return null;
    };

    // handle show
    const handleShow = (val) => {
      show.value = val;
    };

    // enrollee ction
    const handleView = () => {
      return null;
    };

    // add policy button
    const canAddPolicy = computed(() => {
      if (!response.value) return false;

      const end = new Date(response.value.end).getTime();
      const server = new Date(response.value.server_date).getTime();

      if (Number.isNaN(end) || Number.isNaN(server)) return false;

      return server >= end - 30 * 24 * 60 * 60 * 1000;
    });

    // output pages
    const status = computed(() => {
      if (!response.value) return false;

      return (
        response.value.status == "a" &&
        new Date(response.value.end) > new Date(response.value.server_date)
      );
    });

    const closedOrPending = computed(() => {
      return history.value.server_date > history.value.end ? "Closed" : "Pending";
    });

    const statusLabel = computed(() => {
      if (status.value) return "Policy Year Is Active";
      return `${props.cat == "tpa" ? "TPA" : "Policy"} Year Is ${closedOrPending.value}`;
    });

    const statusMessage = computed(() => {
      if (status.value) {
        return "This client can continue normal onboarding, invoicing, and enrollee administration within the active policy window.";
      }

      if (closedOrPending.value == "Closed") {
        return "The current policy window has ended. Start a new policy year when the client is ready for renewal.";
      }

      return "This policy year is pending and should be reviewed before activation-dependent work continues.";
    });

    const previousPolicies = computed(() => {
      return history.value.history.filter((old) => old.id != history.value.id);
    });

    // output pages
    const history = computed(() => {
      if (!response.value) {
        return {
          history: [],
          start: null,
          end: null,
          server_date: new Date().toISOString().split("T")[0],
        };
      } else {
        return response.value;
      }
    });

    return {
      handleShow,
      handleView,
      handleDownload,
      handleNewPolicy,
      handleRenewalConfirmOpen,
      handleRenewalConfirm,
      alertTitle,
      alertMessage,
      canAddPolicy,
      closedOrPending,
      formatDate,
      isReady,
      isRenewing,
      error,
      show,
      status,
      statusLabel,
      statusMessage,
      history,
      isRenewalConfirmOpen,
      previousPolicies,
    };
  },
};
</script>

<style scoped>
.status-workspace {
  margin-top: 8px;
}

.status-head,
.status-card,
.history-card,
.empty-state {
  background: #ffffff;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  box-shadow: none;
}

.status-head {
  align-items: flex-start;
  display: flex;
  gap: 18px;
  justify-content: space-between;
  margin-bottom: 14px;
  padding: 24px;
}

.status-head h5,
.status-card h6,
.history-card h6 {
  color: #263238;
  font-weight: 700;
  margin: 4px 0 0;
}

.status-head h5 {
  font-size: 1.18rem;
  font-weight: 650;
  line-height: 1.25;
}

.status-head p,
.status-card p {
  color: #607d8b;
  font-size: 0.94rem;
  line-height: 1.55;
  margin: 7px 0 0;
}

.status-pill {
  align-items: center;
  background: #e8f5e9;
  border: 1px solid #d7ead9;
  border-radius: 20px;
  color: #2e7d32;
  display: inline-flex;
  flex-shrink: 0;
  font-size: 0.82rem;
  font-weight: 700;
  gap: 6px;
  padding: 6px 11px;
}

.status-pill.pending {
  background: #fff8e1;
  border-color: #ffecb3;
  color: #ef6c00;
}

.status-pill i {
  font-size: 17px;
}

.status-error-alert {
  align-items: center;
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 6px;
  color: #b71c1c;
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
  padding: 11px 14px;
}

.status-error-alert i {
  font-size: 19px;
}

.status-grid {
  display: grid;
  gap: 14px;
  grid-template-columns: minmax(260px, 0.9fr) minmax(320px, 1.1fr);
  margin-bottom: 14px;
}

.status-card,
.history-card {
  padding: 24px;
}

.card-title-row {
  align-items: flex-start;
  border-bottom: 1px solid #eeeeee;
  display: flex;
  justify-content: space-between;
  margin-bottom: 14px;
  padding-bottom: 14px;
}

.card-title-icon {
  color: #90a4ae;
  font-size: 24px;
}

.date-grid {
  display: grid;
  gap: 12px;
  grid-template-columns: repeat(2, minmax(130px, 1fr));
}

.date-box {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  padding: 13px 14px;
}

.date-box span,
.history-item span,
.action-card small {
  color: #78909c;
  display: block;
  font-size: 0.8rem;
  font-weight: 700;
}

.date-box strong,
.history-item strong {
  color: #263238;
  display: block;
  font-weight: 700;
  margin-top: 5px;
}

.action-card {
  display: grid;
  gap: 14px;
}

.action-card button {
  justify-self: start;
}

.renewal-confirm-overlay {
  align-items: center;
  background: rgba(38, 50, 56, 0.48);
  bottom: 0;
  display: flex;
  justify-content: center;
  left: 0;
  padding: 24px;
  position: fixed;
  right: 0;
  top: 0;
  z-index: 1800;
}

.renewal-confirm-modal {
  background: #ffffff;
  border: 1px solid #d7e1e5;
  border-radius: 6px;
  box-shadow: 0 24px 60px rgba(38, 50, 56, 0.28);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 96px);
  max-width: 720px;
  width: min(720px, 100%);
}

.renewal-confirm-content {
  overflow-y: auto;
  padding: 34px 36px 22px;
}

.renewal-confirm-content h4 {
  color: #263238;
  font-size: 2.2rem;
  font-weight: 500;
  letter-spacing: 0;
  margin: 0 0 18px;
}

.renewal-confirm-content p {
  color: #263238;
  font-size: 1rem;
  line-height: 1.55;
  margin: 0;
}

.renewal-confirm-footer {
  align-items: center;
  border-top: 1px solid #e1e8eb;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding: 12px 18px;
}

.renewal-confirm-btn {
  background: transparent;
  border: 1px solid transparent;
  border-radius: 5px;
  color: #37474f;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 800;
  min-height: 36px;
  padding: 0 14px;
  text-transform: uppercase;
}

.renewal-confirm-btn:hover {
  background: #eef3f6;
}

.renewal-confirm-btn.is-confirm {
  color: #00695c;
}

.history-list {
  display: grid;
  gap: 10px;
}

.history-item {
  background: #fafafa;
  border: 1px solid #eeeeee;
  border-radius: 6px;
  padding: 13px 14px;
}

.empty-state {
  color: #607d8b;
  padding: 30px 16px;
  text-align: center;
}

.empty-state i {
  color: #90a4ae;
  font-size: 34px;
}

.empty-state h6 {
  color: #263238;
  font-weight: 700;
  margin: 8px 0 0;
}

@media (max-width: 850px) {
  .status-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 620px) {
  .status-head {
    flex-direction: column;
  }

  .date-grid {
    grid-template-columns: 1fr;
  }
}
</style>
